/**
 * ExerciseCard — generic container for any exercise.
 *
 * Slots for prompt, options, feedback, and controls. The page composes this
 * with SolfegeButtons or MultipleChoiceOptions depending on exercise type.
 */

import { type ReactNode } from "react";

export interface ExerciseCardProps {
  /** Top-right metadata, e.g. "Question 5". */
  status?: ReactNode;
  /** Top-left context, e.g. "Key: B♭ major". */
  context?: ReactNode;
  /** The question text. */
  prompt: string;
  /** Player controls — start/replay/next buttons. */
  controls: ReactNode;
  /** Stage indicator pips for cadence playback. */
  stagePips?: ReactNode;
  /** The answer options (SolfegeButtons or MultipleChoiceOptions). */
  options: ReactNode;
  /** Feedback after answering. */
  feedback: ReactNode;
  /** Optional footer like stats or settings toggles. */
  footer?: ReactNode;
}

export function ExerciseCard(props: ExerciseCardProps): ReactNode {
  return (
    <div className="bg-paper border border-ink/20 p-6 sm:p-8">
      <header className="flex justify-between items-baseline gap-3 pb-4 mb-5 border-b border-ink/10">
        <div className="text-sm text-inkSoft">{props.context}</div>
        <div className="font-mono text-xs text-inkFaded">{props.status}</div>
      </header>

      {props.stagePips && <div className="mb-5">{props.stagePips}</div>}

      <p className="font-serif italic text-lg text-ink mb-5 min-h-[28px]">{props.prompt}</p>

      <div className="mb-6">{props.controls}</div>

      <div className="mb-5" data-testid="answer-options">{props.options}</div>

      <div>{props.feedback}</div>

      {props.footer && <div className="mt-6 pt-5 border-t border-ink/10">{props.footer}</div>}
    </div>
  );
}
